<template>
  <div class="flex flex-col h-full">
    <div class="px-4 py-2.5 bg-white flex items-center gap-3" style="border-bottom: 1px solid #E8E9E9;">
      <h2 class="text-lg font-semibold" style="color: #353537;">Summary</h2>
    </div>

    <div style="border-bottom: 1px solid #E8E9E9;">
      <div class="px-4 pt-3 pb-1">
        <div class="flex items-center gap-3">
          <span class="text-sm font-semibold whitespace-nowrap" style="color: #353537;">Work and signal outcomes, by {{ period }}</span>
          <div class="flex flex-shrink-0 items-center rounded border overflow-hidden text-xs" style="border-color: #E8E9E9;">
            <button
              v-for="option in PERIOD_OPTIONS"
              :key="option.value"
              class="px-2.5 py-1 whitespace-nowrap"
              :style="period === option.value ? 'background: #21426C; color: #fff;' : 'background: #fff; color: #626264;'"
              @click="setPeriod(option.value)"
            >{{ option.label }}</button>
          </div>
          <div class="w-64 flex-shrink-0">
            <SimpleMultiSelect
              v-model="workTypeFilter"
              :options="workTypeOptions"
              placeholder="All work"
              @change="queueTrendFetch"
            />
          </div>
          <div class="w-72 flex-shrink-0">
            <SimpleMultiSelect
              v-model="measureFilter"
              :options="measureOptions"
              placeholder="All measures"
              :searchable="true"
              @change="queueTrendFetch"
            />
          </div>
        </div>
        <div class="mt-1 text-xs" style="color: #A5A5A6;">
          {{ period === 'month' ? `last ${trend ? trend.length : 7} months, current month to date` : `last ${trend ? trend.length : 26} complete weeks` }} &middot;
          work: {{ workTypeFilter.length ? workTypeOptions.filter(o => workTypeFilter.includes(o.value)).map(o => o.label).join(', ') : 'care gap MRR, patient call and action outcome, plus medication adherence pharmacy, patient and provider calls' }} &middot;
          signal outcomes: worked gaps shown compliant in the first payer report after the work (counted in the report's {{ period }}), plus adherence refills dated the day of the call (counted in the call's {{ period }})
        </div>
      </div>
      <div v-if="summary" class="px-4 pt-2 pb-1 flex gap-3">
        <div class="rounded border px-4 py-2.5 min-w-[180px]" style="border-color: #E8E9E9;">
          <div class="text-xs" style="color: #626264;">Work</div>
          <div class="text-2xl font-semibold tabular-nums" style="color: #21426C;">{{ summary.work.toLocaleString() }}</div>
          <div class="text-xs" style="color: #A5A5A6;">{{ windowLabel }}</div>
        </div>
        <div class="rounded border px-4 py-2.5 min-w-[220px]" style="border-color: #E8E9E9;">
          <div class="text-xs" style="color: #626264;">With confirmed payer outcome</div>
          <div class="text-2xl font-semibold tabular-nums" style="color: #389A6C;">{{ summary.pct }}</div>
          <div class="text-xs" style="color: #A5A5A6;">{{ summary.withSignal.toLocaleString() }} of {{ summary.work.toLocaleString() }} work items</div>
        </div>
        <div v-if="lift" class="rounded border px-4 py-2.5 min-w-[340px]" style="border-color: #E8E9E9;">
          <div class="text-xs" style="color: #626264;">Lift vs matched unworked</div>
          <div v-if="lift.total.measurable" class="text-2xl font-semibold tabular-nums" style="color: #21426C;">
            {{ lift.total.extra >= 0 ? '+' : '' }}{{ Math.round(lift.total.extra).toLocaleString() }}
            <span class="text-sm font-normal" style="color: #626264;">extra outcomes (&plusmn;{{ Math.round(lift.total.ci) }})</span>
          </div>
          <div v-else class="text-2xl font-semibold" style="color: #A5A5A6;">No measurable lift</div>
          <div v-for="row in lift.rows" :key="row.program" class="text-xs tabular-nums" style="color: #626264;">
            {{ row.label }}: {{ row.actualPct }} vs {{ row.expectedPct }} expected
            <span :style="{ color: row.measurable ? '#23734F' : '#A5A5A6' }">({{ row.pointsLabel }})</span>
            &middot; n {{ row.matched.toLocaleString() }}{{ row.matched < row.units ? ` of ${row.units.toLocaleString()}` : '' }}
          </div>
        </div>
      </div>
      <div class="px-2 pb-2 relative">
        <LoadingIndicator v-if="loading && !trend" />
        <div v-else-if="error" class="px-2 py-6 text-sm" style="color: #B91C1C;">{{ error }}</div>
        <div v-else-if="!trend || !trend.some(w => w.work_count)" class="px-2 py-6 text-sm" style="color: #626264;">
          No work logged in this window.
        </div>
        <Chart_V5 v-else :chart_data="trendChart" :chart_config="trendConfig" override-height="h-[320px]" />
        <div
          v-if="loading && trend"
          class="absolute inset-0 flex items-center justify-center"
          style="background: rgba(255, 255, 255, 0.6);"
        >
          <LoadingIndicator />
        </div>
      </div>
    </div>

    <div>
      <div class="px-4 pt-3 pb-2 flex items-center gap-3">
        <span class="text-sm font-semibold" style="color: #353537;">Work &rarr; signal outcome</span>
        <span class="text-xs" style="color: #A5A5A6;">
          every piece of work in the last 26 weeks, newest first, with the signal outcome tied to it
        </span>
        <div class="flex-1"></div>
        <label class="flex items-center gap-1.5 text-xs" style="color: #626264;">
          <input v-model="signalOnly" type="checkbox" @change="changeSignalFilter" />
          Signal outcomes only
        </label>
        <span class="text-xs tabular-nums" style="color: #626264;">{{ logTotal.toLocaleString() }} rows</span>
      </div>
      <LoadingIndicator v-if="logLoading" />
      <div v-else-if="logError" class="px-4 py-6 text-sm" style="color: #B91C1C;">{{ logError }}</div>
      <div v-else-if="!logRows.length" class="px-4 py-6 text-sm" style="color: #626264;">No work logged in this window.</div>
      <div v-else class="px-4 pb-4 overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs" style="color: #626264; border-bottom: 1px solid #E8E9E9;">
              <th class="py-2 pr-4 font-medium">When</th>
              <th class="py-2 pr-4 font-medium">Worker</th>
              <th class="py-2 pr-4 font-medium">Work</th>
              <th class="py-2 pr-4 font-medium">Outcome logged</th>
              <th class="py-2 pr-4 font-medium">Patient</th>
              <th class="py-2 pr-4 font-medium">DOB</th>
              <th class="py-2 pr-4 font-medium">Measure</th>
              <th class="py-2 pr-4 font-medium">Contract</th>
              <th class="py-2 pr-4 font-medium">Practice</th>
              <th class="py-2 pr-4 font-medium">Signal outcome</th>
              <th class="py-2 font-medium">Signal date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in logRows" :key="row.work_id" style="border-bottom: 1px solid #F3F4F4;">
              <td class="py-1.5 pr-4 whitespace-nowrap tabular-nums">{{ formatDateTime(row.work_created_at) }}</td>
              <td class="py-1.5 pr-4 whitespace-nowrap sensitive">{{ row.worker_entity_name || '—' }}</td>
              <td class="py-1.5 pr-4 whitespace-nowrap">{{ row.work_type }}</td>
              <td class="py-1.5 pr-4">{{ formatOutcome(row.outcome) }}</td>
              <td class="py-1.5 pr-4 whitespace-nowrap sensitive">{{ [row.patient_last_name, row.patient_first_name].filter(Boolean).join(', ') || '—' }}</td>
              <td class="py-1.5 pr-4 whitespace-nowrap tabular-nums sensitive">{{ row.patient_dob || '—' }}</td>
              <td class="py-1.5 pr-4 whitespace-nowrap">{{ row.measure_code || '—' }}</td>
              <td class="py-1.5 pr-4">{{ row.contract_name || '—' }}</td>
              <td class="py-1.5 pr-4 sensitive">{{ row.practice_name || '—' }}</td>
              <td class="py-1.5 pr-4" :style="{ color: row.has_signal ? '#23734F' : '#626264', fontWeight: row.has_signal ? 600 : 400 }">{{ row.signal_outcome }}</td>
              <td class="py-1.5 whitespace-nowrap tabular-nums">{{ row.signal_date || '' }}</td>
            </tr>
          </tbody>
        </table>
        <div class="flex items-center justify-end gap-3 pt-3 text-xs" style="color: #626264;">
          <span class="tabular-nums">{{ (logStart + 1).toLocaleString() }}–{{ Math.min(logStart + LOG_PAGE_SIZE, logTotal).toLocaleString() }} of {{ logTotal.toLocaleString() }}</span>
          <button class="px-2 py-1 rounded border" style="border-color: #E8E9E9;" :disabled="logStart === 0" @click="pageLog(-1)">Previous</button>
          <button class="px-2 py-1 rounded border" style="border-color: #E8E9E9;" :disabled="logStart + LOG_PAGE_SIZE >= logTotal" @click="pageLog(1)">Next</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import abortable from '@/mixins/abortable';

const LOG_PAGE_SIZE = 100;
const FILTER_SETTLE_MS = 600;
const LIFT_MIN_UNITS = 30;
const LIFT_PROGRAMS = [
  { program: 'med_adherence', label: 'Adherence, refilled same day' },
  { program: 'care_gap', label: 'Care gaps, closed in payer report' },
];

const PERIOD_OPTIONS = [
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
];

export default {
  name: 'Quality_Summary',
  components: { Chart_V5, LoadingIndicator, SimpleMultiSelect },
  mixins: [abortable],

  data() {
    return {
      trend: null,
      period: 'week',
      PERIOD_OPTIONS,
      workTypeFilter: [],
      workTypeOptions: [],
      measureFilter: [],
      measureOptions: [],
      liftUnits: [],
      loading: false,
      error: null,
      logRows: [],
      logTotal: 0,
      logStart: 0,
      logLoading: false,
      logError: null,
      signalOnly: false,
      LOG_PAGE_SIZE,
    };
  },

  computed: {
    lift() {
      if (!this.liftUnits.length) return null;
      const types = new Set(this.workTypeFilter);
      const measures = new Set(this.measureFilter);
      const selected = this.liftUnits.filter(u =>
        (!types.size || u.work_types.some(t => types.has(t))) && (!measures.size || measures.has(u.measure_code))
      );
      if (!selected.length) return null;
      const summarize = units => {
        const matched = units.filter(u => u.expected !== null);
        const actual = matched.reduce((a, u) => a + u.outcome, 0);
        const expected = matched.reduce((a, u) => a + u.expected, 0);
        const variance = matched.reduce((a, u) => a + u.expected * (1 - u.expected), 0);
        const extra = actual - expected;
        const ci = 1.96 * Math.sqrt(variance);
        const n = matched.length;
        const points = n ? ((actual - expected) / n) * 100 : 0;
        return {
          units: units.length,
          matched: n,
          extra,
          ci,
          measurable: n >= LIFT_MIN_UNITS && Math.abs(extra) > ci,
          actualPct: n ? `${((actual / n) * 100).toFixed(1)}%` : '—',
          expectedPct: n ? `${((expected / n) * 100).toFixed(1)}%` : '—',
          pointsLabel: `${points >= 0 ? '+' : ''}${points.toFixed(1)} pts`,
        };
      };
      const rows = LIFT_PROGRAMS
        .map(p => ({ ...p, units: selected.filter(u => u.program === p.program) }))
        .filter(p => p.units.length)
        .map(p => ({ program: p.program, label: p.label, ...summarize(p.units) }));
      return { total: summarize(selected), rows };
    },
    summary() {
      if (!this.trend || !this.trend.length) return null;
      const work = this.trend.reduce((a, w) => a + w.work_count, 0);
      const withSignal = this.trend.reduce((a, w) => a + w.work_with_signal, 0);
      return { work, withSignal, pct: work ? `${((withSignal / work) * 100).toFixed(1)}%` : '—' };
    },
    windowLabel() {
      const n = this.trend ? this.trend.length : 0;
      return this.period === 'month' ? `last ${n} months, current month to date` : `last ${n} complete weeks`;
    },
    trendChart() {
      return {
        labels: this.trend.map(w => this.formatPeriod(w)),
        series: [
          {
            values: this.trend.map(w => w.work_count),
            config: {
              label: 'Work (left axis)',
              backgroundColor: '#21426C',
              type: 'line',
              yAxis: 'left',
              offset: 0,
            },
          },
          {
            values: this.trend.map(w => w.signal_outcomes),
            config: {
              label: 'Signal outcomes (right axis)',
              backgroundColor: '#389A6C',
              type: 'line',
              yAxis: 'right',
              offset: 0,
            },
          },
        ],
      };
    },
    trendConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 60, right: 70, bottom: 70, left: 60 },
        xAxis: { labelRotation: -45, fontSize: 11 },
        yAxes: [
          { position: 'left', min: 0, max: null, fontSize: 11 },
          { position: 'right', min: 0, max: null, fontSize: 11 },
        ],
        legend: { display: true, position: 'top', spacing: 15 },
        formatting: { percentage: false, currency: false, sig_figs: 0 },
        labelDisplayConfig: { minLabelSpacing: 1, labelFontSize: 11 },
      };
    },
  },

  beforeUnmount() {
    clearTimeout(this._trendTimer);
  },

  mounted() {
    this.fetchTrend();
    this.fetchLog();
    this.fetchLiftUnits();
  },

  methods: {
    formatOutcome(outcome) {
      if (!outcome) return '—';
      if (!outcome.includes('_')) return outcome;
      const s = outcome.replace(/_+$/, '').replace(/_/g, ' ');
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    setPeriod(value) {
      if (value === this.period) return;
      this.period = value;
      this.fetchTrend();
    },
    formatPeriod(row) {
      if (this.period === 'week') return row.period_start;
      const [year, month] = row.period_start.split('-').map(Number);
      const label = new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
      return row.is_partial ? `${label} (to date)` : label;
    },
    formatDateTime(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    },
    changeSignalFilter() {
      this.logStart = 0;
      this.fetchLog();
    },
    pageLog(direction) {
      this.logStart = Math.max(0, this.logStart + direction * LOG_PAGE_SIZE);
      this.fetchLog();
    },
    async fetchLog() {
      this.logLoading = true;
      this.logError = null;
      try {
        const { data } = await this.$axios.get('/api/quality/operations/', {
          params: {
            lookup: 'work_signal_log',
            startRow: this.logStart,
            endRow: this.logStart + LOG_PAGE_SIZE,
            signalOnly: this.signalOnly,
          },
        });
        this.logRows = data.rows;
        this.logTotal = data.total;
      } catch (e) {
        this.logError = e?.response?.data?.error || 'Could not load work log.';
        this.logRows = [];
        this.logTotal = 0;
      } finally {
        this.logLoading = false;
      }
    },
    async fetchLiftUnits() {
      try {
        const { data } = await this.$axios.get('/api/quality/operations/', { params: { lookup: 'lift_units' } });
        this.liftUnits = data.units;
      } catch (e) {
        this.liftUnits = [];
      }
    },
    queueTrendFetch() {
      clearTimeout(this._trendTimer);
      this._trendTimer = setTimeout(() => this.fetchTrend(), FILTER_SETTLE_MS);
    },
    async fetchTrend() {
      clearTimeout(this._trendTimer);
      const signal = this._renewAbortSignal('trend');
      this.loading = true;
      this.error = null;
      try {
        const { data } = await this.$axios.get('/api/quality/operations/', {
          params: { lookup: 'weekly_trend', period: this.period, workTypes: this.workTypeFilter.join(','), measures: this.measureFilter.join('|') },
          signal,
        });
        this.trend = data.trend;
        if (!this.workTypeOptions.length) {
          this.workTypeOptions = data.work_type_options.map(o => ({ value: o.value, label: `${o.label} (${o.program})` }));
        }
        if (!this.measureOptions.length) this.measureOptions = data.measure_options;
      } catch (e) {
        if (this._isAborted(e)) return;
        this.error = e?.response?.data?.error || 'Could not load work trend.';
        this.trend = null;
      } finally {
        if (!signal.aborted) this.loading = false;
      }
    },

  },
};
</script>
